import { useState, useMemo } from "react";
import {
  uploadProofOfIncomeFiles,
  deleteProofOfIncomeFiles,
} from "@/lib/api/applicationService";
import { uploadNoBankNinja } from "@/lib/api/bankService";
import { showSuccess, showError } from "@/lib/utils/notifications";
import {
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
  isAcceptedIncomeOrIdDocumentFile,
} from "@/lib/utils/acceptedDocumentFile";
import {
  buildIncomeVerificationDocuments,
  getNinjaIncomeProofEditFiles,
  getProofOfIncomeEditFiles,
  getStoredNinjaIncomeProofCount,
  getStoredProofOfIncomeCount,
  normalizeIncomeAttachmentList,
  resolveSupportingIncomeUploadMode,
} from "../incomeEmploymentHelpers";
import type { IncomeAttachmentFile } from "../../../../types/incomeEmploymentApi";

const MAX_NINJA_INCOME_FILES = 6;
const MAX_PROOF_OF_INCOME_FILES = 6;
const MAX_NINJA_FILE_MB = 5;
const MAX_PROOF_FILE_MB = 10;

export function useIncomeDocuments(options: {
  applicantId: string;
  applicationData: {
    applicant_status?: string;
    bank_name?: string;
    ninja_income_proof?: IncomeAttachmentFile[];
    proof_of_income?: IncomeAttachmentFile[];
  };
  onRefresh?: () => void | Promise<void>;
}) {
  const { applicantId, applicationData, onRefresh } = options;
  const [proofOfIncomeUploading, setProofOfIncomeUploading] = useState(false);

  const incomeVerificationDocuments = useMemo(
    () => buildIncomeVerificationDocuments(applicationData),
    [applicationData],
  );

  const ninjaIncomeDocuments = useMemo(
    () => getNinjaIncomeProofEditFiles(applicationData),
    [applicationData],
  );

  const proofIncomeDocuments = useMemo(
    () => getProofOfIncomeEditFiles(applicationData),
    [applicationData],
  );

  const storedNinjaIncomeProofCount = useMemo(
    () => getStoredNinjaIncomeProofCount(applicationData),
    [applicationData],
  );

  const storedProofOfIncomeCount = useMemo(
    () => getStoredProofOfIncomeCount(applicationData),
    [applicationData],
  );

  const supportingIncomeUploadMode = resolveSupportingIncomeUploadMode(
    applicationData,
    true,
  );

  const handleIncomeDocumentFiles = async (files: FileList | File[]) => {
    const selected = Array.from(files || []);
    if (selected.length === 0) return;

    const invalidType = selected.find((f) => !isAcceptedIncomeOrIdDocumentFile(f));
    if (invalidType) {
      showError(ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR);
      return;
    }

    const uploadMode = supportingIncomeUploadMode;
    if (!uploadMode) {
      showError("File upload is not available for your current application status.");
      return;
    }

    if (uploadMode === "ninja") {
      const existing = normalizeIncomeAttachmentList(
        applicationData.ninja_income_proof,
      );
      const existingNames = new Set(
        existing.map((f) => (f.filename || f.name || "").toLowerCase()).filter(Boolean),
      );
      const slots = Math.max(0, MAX_NINJA_INCOME_FILES - storedNinjaIncomeProofCount);
      if (slots <= 0) {
        showError(
          "You have exceed the six file maximum upload. Please upload no more than six files, and if you need further assistance please contact help@libertyrent.com.",
        );
        return;
      }

      const seen = new Set<string>();
      const valid: File[] = [];
      for (const file of selected) {
        const name = file.name.toLowerCase();
        if (existingNames.has(name) || seen.has(name)) {
          showError("Sorry, you can not upload the same document more than once!");
          return;
        }
        if (file.size / 1024 / 1024 > MAX_NINJA_FILE_MB) {
          showError(`File Size Exceeds Allowable Limit of ${MAX_NINJA_FILE_MB} MB.`);
          return;
        }
        seen.add(name);
        valid.push(file);
      }

      const filesToUpload = valid.slice(0, slots);
      if (filesToUpload.length === 0) return;

      const bankName = `${applicationData.bank_name ?? ""}`.trim();
      setProofOfIncomeUploading(true);
      try {
        await uploadNoBankNinja(applicantId, bankName, filesToUpload);
        showSuccess("Files uploaded successfully.");
        if (onRefresh) await onRefresh();
      } catch (error: unknown) {
        showError(
          error instanceof Error ? error.message : "Failed to upload files.",
        );
      } finally {
        setProofOfIncomeUploading(false);
      }
      return;
    }

    const existingProofCount = storedProofOfIncomeCount;
    const totalAfterUpload = existingProofCount + selected.length;
    if (totalAfterUpload > MAX_PROOF_OF_INCOME_FILES) {
      showError(
        "You have exceed the six file maximum upload. Please upload no more than six files, and if you need further assistance please contact help@libertyrent.com.",
      );
      return;
    }

    const oversized = selected.find((f) => f.size / 1024 / 1024 > MAX_PROOF_FILE_MB);
    if (oversized) {
      showError(`File Size Exceeds Allowable Limit of ${MAX_PROOF_FILE_MB} MB.`);
      return;
    }

    setProofOfIncomeUploading(true);
    try {
      await uploadProofOfIncomeFiles(applicantId, selected);
      showSuccess("Files uploaded successfully.");
      if (onRefresh) await onRefresh();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to upload files.",
      );
    } finally {
      setProofOfIncomeUploading(false);
    }
  };

  const handleRemoveProofOfIncome = async (attachmentId: string | number) => {
    try {
      await deleteProofOfIncomeFiles(applicantId, [attachmentId]);
      showSuccess("File removed.");
      if (onRefresh) await onRefresh();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to remove file.",
      );
    }
  };

  return {
    proofOfIncomeUploading,
    incomeVerificationDocuments,
    ninjaIncomeDocuments,
    proofIncomeDocuments,
    storedNinjaIncomeProofCount,
    storedProofOfIncomeCount,
    supportingIncomeUploadMode,
    handleIncomeDocumentFiles,
    handleRemoveProofOfIncome,
  };
}
